import { useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Chip,
  ListItemText,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import GridOnIcon from '@mui/icons-material/GridOn';
import autoTable from 'jspdf-autotable';
import { type GridColDef } from '@mui/x-data-grid';
import { useTranslation } from 'react-i18next';
import { PageHeader } from '../../../../shared/components/PageHeader';
import { DataTable, type DataTableQuery } from '../../../../shared/components/DataTable';
import { ErrorState } from '../../../../shared/components/ErrorState';
import { ColaboradorSelect } from '../../../../shared/components/ColaboradorSelect';
import { ClienteSelect } from '../../../../shared/components/ClienteSelect';
import {
  MonthFilterField,
  fromMonthValue,
  toMonthValue,
} from '../../../../shared/components/MonthFilterField';
import { GRID_PAGE_SIZE, usePersistedGridState } from '../../../../shared/grid/usePersistedGridState';
import { describeApiError } from '../../../../shared/api/errors';
import { useToast } from '../../../../shared/stores/toast.store';
import { useAuth } from '../../../../shared/auth/useAuth';
import { downloadAsPdf } from '../../../../shared/reports/downloadAsPdf';
import { downloadAsXlsx } from '../../../../shared/reports/downloadAsXlsx';
import { buildFilename, formatMoney } from '../../../../shared/reports/formatters';
import { listExtratoFaturamento } from '../api';
import { useExtratoFaturamento, useExtratoFaturamentoTotais } from '../hooks';
import type { ExtratoFaturamentoFiltros, ExtratoFaturamentoItem, ExtratoNfStatus } from '../types';

const DEFAULT_QUERY: DataTableQuery = {
  page: 1,
  pageSize: GRID_PAGE_SIZE,
  sortBy: 'colaboradorNome',
  sortOrder: 'asc',
};

const DEFAULT_FILTROS: ExtratoFaturamentoFiltros = { status: [] };

/** Domínio StatusNotaFiscal completo (labels via i18n `statusLabel.*`). */
const STATUS_OPTIONS: ExtratoNfStatus[] = [
  'NSO',
  'SOL',
  'PAP',
  'APR',
  'REP',
  'CAN',
  'CON',
  'EMI',
  'PCA',
];

/** Teto do export client-side — acima disso, truncamos e avisamos. */
const EXPORT_MAX_ROWS = 1000;

function statusChipColor(s: ExtratoNfStatus): 'default' | 'success' | 'warning' | 'info' | 'error' {
  switch (s) {
    case 'APR':
    case 'EMI':
    case 'CON':
      return 'success';
    case 'PAP':
      return 'info';
    case 'SOL':
      return 'warning';
    case 'REP':
      return 'error';
    default:
      return 'default';
  }
}

export function EmpresaExtratoFaturamentoPage(): JSX.Element {
  const { t } = useTranslation();
  const toast = useToast();
  const { user } = useAuth();

  const {
    query,
    setQuery,
    filters: filtros,
    patchFilters: patchFiltros,
    clear,
  } = usePersistedGridState('empresa-extrato-faturamento', DEFAULT_QUERY, DEFAULT_FILTROS);
  const [exporting, setExporting] = useState(false);

  const listQuery = useExtratoFaturamento(query, filtros);
  const totaisQuery = useExtratoFaturamentoTotais(filtros, query.search);

  const columns: GridColDef<ExtratoFaturamentoItem>[] = [
    {
      field: 'colaboradorNome',
      headerName: t('features.empresa.extratoFaturamento.columns.colaborador'),
      flex: 1.5,
      minWidth: 200,
    },
    {
      field: 'clienteNome',
      headerName: t('features.empresa.extratoFaturamento.columns.cliente'),
      flex: 1.5,
      minWidth: 180,
      valueFormatter: (v: string | null) => v ?? '—',
    },
    {
      field: 'periodo',
      headerName: t('features.empresa.extratoFaturamento.columns.periodo'),
      flex: 0.7,
      minWidth: 120,
    },
    {
      field: 'valorReceber',
      headerName: t('features.empresa.extratoFaturamento.columns.valorReceber'),
      flex: 0.8,
      minWidth: 130,
      align: 'right',
      headerAlign: 'right',
      valueFormatter: (v: string) => formatMoney(v),
    },
    {
      field: 'valorRecebido',
      headerName: t('features.empresa.extratoFaturamento.columns.valorRecebido'),
      flex: 0.8,
      minWidth: 130,
      align: 'right',
      headerAlign: 'right',
      valueFormatter: (v: string) => formatMoney(v),
    },
    {
      field: 'valorPendente',
      headerName: t('features.empresa.extratoFaturamento.columns.valorPendente'),
      flex: 0.8,
      minWidth: 130,
      align: 'right',
      headerAlign: 'right',
      renderCell: (params) => {
        const pendente = Number(String(params.value).replace(',', '.')) > 0;
        return (
          <Typography
            component="span"
            variant="body2"
            fontWeight={pendente ? 600 : 400}
            color={pendente ? 'warning.main' : 'text.primary'}
            sx={{ display: 'inline-flex', alignItems: 'center', height: '100%' }}
          >
            {formatMoney(params.value as string)}
          </Typography>
        );
      },
    },
    {
      field: 'faturamentoParcial',
      headerName: t('features.empresa.extratoFaturamento.columns.parcial'),
      flex: 0.45,
      minWidth: 80,
      align: 'center',
      headerAlign: 'center',
      valueFormatter: (v: boolean) =>
        v
          ? t('features.empresa.extratoFaturamento.parcialSim')
          : t('features.empresa.extratoFaturamento.parcialNao'),
    },
    {
      field: 'status',
      headerName: t('features.empresa.extratoFaturamento.columns.status'),
      flex: 1.1,
      minWidth: 200,
      renderCell: (params) => (
        <Chip
          size="small"
          variant="outlined"
          color={statusChipColor(params.row.status)}
          label={params.row.statusLabel || params.row.status}
        />
      ),
    },
  ];

  async function fetchAllForExport(): Promise<ExtratoFaturamentoItem[]> {
    const all = await listExtratoFaturamento(
      { ...query, page: 1, pageSize: EXPORT_MAX_ROWS },
      filtros,
    );
    if (all.meta.total > EXPORT_MAX_ROWS) {
      toast.info(
        t('features.empresa.extratoFaturamento.export.truncado', { max: EXPORT_MAX_ROWS }),
      );
    }
    return all.data;
  }

  async function exportXlsx(): Promise<void> {
    setExporting(true);
    try {
      const rows = await fetchAllForExport();
      downloadAsXlsx(buildFilename('extrato-faturamento-empresa'), [
        {
          name: 'Extrato',
          rows: rows.map((r) => ({
            colaborador: r.colaboradorNome,
            cliente: r.clienteNome ?? '',
            periodo: r.periodo,
            valorReceber: Number(r.valorReceber),
            valorRecebido: Number(r.valorRecebido),
            valorPendente: Number(r.valorPendente),
            parcial: r.faturamentoParcial
              ? t('features.empresa.extratoFaturamento.parcialSim')
              : t('features.empresa.extratoFaturamento.parcialNao'),
            status: r.statusLabel,
          })),
          columns: [
            { key: 'colaborador', header: 'Colaborador', width: 28 },
            { key: 'cliente', header: 'Cliente', width: 26 },
            { key: 'periodo', header: 'Período', width: 16 },
            { key: 'valorReceber', header: 'Valor a Receber', format: 'money', width: 16 },
            { key: 'valorRecebido', header: 'Valor Recebido', format: 'money', width: 16 },
            { key: 'valorPendente', header: 'Valor Pendente', format: 'money', width: 16 },
            { key: 'parcial', header: 'Parcial', width: 10 },
            { key: 'status', header: 'Status', width: 22 },
          ],
          freezeHeader: true,
        },
      ]);
    } catch (err) {
      toast.error(describeApiError(err));
    } finally {
      setExporting(false);
    }
  }

  async function exportPdf(): Promise<void> {
    setExporting(true);
    try {
      const rows = await fetchAllForExport();
      const totais = totaisQuery.data;
      downloadAsPdf(
        buildFilename('extrato-faturamento-empresa'),
        {
          title: t('features.empresa.extratoFaturamento.title'),
          subtitle: t('features.empresa.extratoFaturamento.export.subtitle', {
            count: rows.length,
          }),
          usuario: user?.nome ?? '',
        },
        ({ doc, contentStartY }) => {
          autoTable(doc, {
            startY: contentStartY,
            head: [
              [
                'Colaborador',
                'Cliente',
                'Período',
                'A Receber',
                'Recebido',
                'Pendente',
                'Parcial',
                'Status',
              ],
            ],
            body: rows.map((r) => [
              r.colaboradorNome,
              r.clienteNome ?? '',
              r.periodo,
              formatMoney(r.valorReceber),
              formatMoney(r.valorRecebido),
              formatMoney(r.valorPendente),
              r.faturamentoParcial
                ? t('features.empresa.extratoFaturamento.parcialSim')
                : t('features.empresa.extratoFaturamento.parcialNao'),
              r.statusLabel,
            ]),
            foot: totais
              ? [
                  [
                    '',
                    '',
                    'Totais:',
                    formatMoney(totais.totalValorReceber),
                    formatMoney(totais.totalValorRecebido),
                    formatMoney(totais.totalValorPendente),
                    '',
                    '',
                  ],
                ]
              : undefined,
            styles: { fontSize: 7 },
          });
        },
      );
    } catch (err) {
      toast.error(describeApiError(err));
    } finally {
      setExporting(false);
    }
  }

  if (listQuery.isLoadingError) {
    return (
      <>
        <PageHeader title={t('features.empresa.extratoFaturamento.title')} />
        <ErrorState
          description={describeApiError(listQuery.error)}
          onRetry={() => listQuery.refetch()}
        />
      </>
    );
  }

  const totais = totaisQuery.data;

  return (
    <>
      <PageHeader
        title={t('features.empresa.extratoFaturamento.title')}
        subtitle={t('features.empresa.extratoFaturamento.subtitle')}
        breadcrumbs={[
          { label: t('menu.company') },
          { label: t('features.empresa.extratoFaturamento.title') },
        ]}
        actions={
          <Stack direction="row" spacing={1}>
            <Button
              variant="outlined"
              startIcon={<PictureAsPdfIcon />}
              disabled={exporting}
              onClick={() => void exportPdf()}
            >
              PDF
            </Button>
            <Button
              variant="outlined"
              startIcon={<GridOnIcon />}
              disabled={exporting}
              onClick={() => void exportXlsx()}
            >
              XLSX
            </Button>
          </Stack>
        }
      />

      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mb: 2 }}>
        <TotalCard
          label={t('features.empresa.extratoFaturamento.totais.receber')}
          value={totais ? formatMoney(totais.totalValorReceber) : '—'}
        />
        <TotalCard
          label={t('features.empresa.extratoFaturamento.totais.recebido')}
          value={totais ? formatMoney(totais.totalValorRecebido) : '—'}
        />
        <TotalCard
          label={t('features.empresa.extratoFaturamento.totais.pendente')}
          value={totais ? formatMoney(totais.totalValorPendente) : '—'}
          highlight
        />
      </Stack>

      <DataTable<ExtratoFaturamentoItem>
        rows={listQuery.data?.data ?? []}
        columns={columns}
        total={listQuery.data?.meta.total ?? 0}
        loading={listQuery.isLoading || listQuery.isFetching}
        query={query}
        onQueryChange={setQuery}
        onClear={clear}
        searchPlaceholder={t('features.empresa.extratoFaturamento.searchPlaceholder')}
        filters={
          <Box
            sx={{
              flexGrow: 1,
              minWidth: 0,
              display: 'grid',
              gap: 1.5,
              gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
              alignItems: 'start',
            }}
          >
            <ColaboradorSelect
              value={filtros.colaboradorCodigo ?? null}
              onChange={(id) => patchFiltros({ colaboradorCodigo: id })}
              label={t('features.empresa.extratoFaturamento.filtros.colaborador')}
            />
            <ClienteSelect
              value={filtros.clienteCodigo ?? null}
              onChange={(id) => patchFiltros({ clienteCodigo: id })}
              label={t('features.empresa.extratoFaturamento.filtros.cliente')}
            />
            <TextField
              select
              size="small"
              fullWidth
              label={t('features.empresa.extratoFaturamento.filtros.status')}
              value={filtros.status ?? []}
              onChange={(e) => {
                const v = e.target.value as unknown as ExtratoNfStatus[];
                patchFiltros({ status: v });
              }}
              SelectProps={{
                multiple: true,
                renderValue: (selected) => (selected as string[]).join(', '),
              }}
            >
              {STATUS_OPTIONS.map((s) => (
                <MenuItem key={s} value={s}>
                  <Checkbox size="small" checked={(filtros.status ?? []).includes(s)} />
                  <ListItemText
                    primary={t(`features.empresa.extratoFaturamento.statusLabel.${s}`, {
                      defaultValue: s,
                    })}
                  />
                </MenuItem>
              ))}
            </TextField>
            <MonthFilterField
              fullWidth
              label={t('components.monthFilter.competencia')}
              value={toMonthValue(filtros.periodoAno, filtros.periodoMes)}
              onChange={(value) => {
                const parsed = fromMonthValue(value);
                patchFiltros({ periodoAno: parsed.year, periodoMes: parsed.month });
              }}
            />
            <TextField
              select
              size="small"
              fullWidth
              label={t('features.empresa.extratoFaturamento.filtros.parcial')}
              value={
                filtros.faturamentoParcial === undefined || filtros.faturamentoParcial === null
                  ? 'todos'
                  : filtros.faturamentoParcial
                    ? 'sim'
                    : 'nao'
              }
              onChange={(e) =>
                patchFiltros({
                  faturamentoParcial: e.target.value === 'todos' ? null : e.target.value === 'sim',
                })
              }
            >
              <MenuItem value="todos">
                {t('features.empresa.extratoFaturamento.filtros.parcialTodos')}
              </MenuItem>
              <MenuItem value="sim">{t('features.empresa.extratoFaturamento.parcialSim')}</MenuItem>
              <MenuItem value="nao">{t('features.empresa.extratoFaturamento.parcialNao')}</MenuItem>
            </TextField>
          </Box>
        }
      />
    </>
  );
}

function TotalCard({
  label,
  value,
  highlight,
}: {
  label: string;
  value: string;
  highlight?: boolean;
}): JSX.Element {
  return (
    <Paper variant="outlined" sx={{ p: 1.5, flex: 1, minWidth: 180 }}>
      <Typography variant="caption" color="text.secondary" display="block">
        {label}
      </Typography>
      <Typography variant="h6" fontWeight={700} color={highlight ? 'warning.main' : 'text.primary'}>
        {value}
      </Typography>
    </Paper>
  );
}
